import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {Anchor, Text} from '@mantine/core';
import {formatPerson} from '@flexinet/ui-components';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {consoleAccountUrl} from '../config/consoleLinks';
import {UserStatusBadge} from '../profile/UserStatusBadge';
import {User} from '../types';
import config from '../config';

const PLACEHOLDER = '—';

// Every column has an accessor and is sortable: sorting reads the accessor,
// and a render-only column cannot be sorted at all (checklist §3a). Titles are
// translated here, at render, not once when the module is imported.
export const useUsersTableColumns = () => {
    const columns: DataTableColumn<User>[] = [
        {
            accessor: 'givenName',
            sortable: true,
            title: I18n.get('Name'),
            width: '1fr',
            render: (item: User) => (
                <NavLink to={`${config.basePath}/${encodeURIComponent(item.id)}`}>
                    {formatPerson(item) ?? item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'email',
            sortable: true,
            title: I18n.get('Email'),
            width: '1fr',
            render: (item: User) => item.email || PLACEHOLDER,
        },
        {
            accessor: 'accountId',
            sortable: true,
            title: I18n.get('Account'),
            width: '1fr',
            // Read-only, and it opens in the console, which owns the Account.
            render: (item: User) => item.accountId
                ? (
                    <Anchor href={consoleAccountUrl(item.accountId)} onClick={(event) => event.stopPropagation()}>
                        <Text span size={'xs'} ff={'monospace'}>{item.accountId}</Text>
                    </Anchor>
                )
                : PLACEHOLDER,
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('User Status'),
            width: '1fr',
            render: (item: User) => <UserStatusBadge record={item}/>,
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: User) => formatTimestamp(item.created),
        },
        {
            accessor: 'updated',
            sortable: true,
            title: I18n.get('Last Updated'),
            width: '1fr',
            render: (item: User) => formatTimestamp(item.updated),
        },
    ];

    return {columns};
};
